import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useFormResponses } from "@/hooks/useFormResponses";
import { Field, TextInput, YesNo } from "@/components/Field";
import { combineFieldErrors, combineFormError, runUpserts } from "@/lib/validation-errors";

/**
 * Step — Interpreter information (Part 12 of N-400).
 *
 * Filled only when the applicant used an interpreter to fill out the
 * form. Most applicants self-prepare in their fluent language and
 * skip this entire section. The mapping leaves Part 12 blank when no
 * interpreter_* keys are set.
 *
 * The "did you use an interpreter?" toggle isn't stored anywhere
 * (the mapping just looks at whether the family-name field is set),
 * so we derive the toggle from local state + presence of the family
 * name on hydration.
 */

const FR_KEYS = {
  family: "interpreter_family_name",
  given: "interpreter_given_name",
  org: "interpreter_org",
  phone: "interpreter_phone",
  mobile: "interpreter_mobile",
  email: "interpreter_email",
  language: "interpreter_language",
  signatureDate: "interpreter_signature_date",
} as const;

export function StepInterpreter() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fr = useFormResponses(id);

  const [usedInterpreter, setUsedInterpreter] = useState<boolean | null>(null);
  const [family, setFamily] = useState("");
  const [given, setGiven] = useState("");
  const [org, setOrg] = useState("");
  const [phone, setPhone] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [language, setLanguage] = useState("");
  const [signatureDate, setSignatureDate] = useState("");

  useEffect(() => {
    if (fr.isLoading) return;
    const f = fr.getString(FR_KEYS.family);
    setFamily(f);
    setGiven(fr.getString(FR_KEYS.given));
    setOrg(fr.getString(FR_KEYS.org));
    setPhone(fr.getString(FR_KEYS.phone));
    setMobile(fr.getString(FR_KEYS.mobile));
    setEmail(fr.getString(FR_KEYS.email));
    setLanguage(fr.getString(FR_KEYS.language));
    setSignatureDate(fr.getString(FR_KEYS.signatureDate));
    // Derive the toggle from existing data: if any name is set, the
    // user previously said "yes". Otherwise leave it unanswered.
    if (f) setUsedInterpreter(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fr.isLoading, ...Object.values(FR_KEYS).map((k) => fr.get(k))]);

  const [validationErr, setValidationErr] = useState<string | null>(null);
  const [saveErrors, setSaveErrors] = useState<unknown[]>([]);
  const frFieldErrors = combineFieldErrors(saveErrors);
  const frFormError = combineFormError(saveErrors);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationErr(null);
    setSaveErrors([]);

    if (usedInterpreter === null) {
      setValidationErr("Please answer whether you used an interpreter.");
      return;
    }

    const upserts: Promise<unknown>[] = [];
    const upsertStr = (key: string, val: string) => {
      if (val) upserts.push(fr.upsert.mutateAsync({ fieldKey: key, value: val }));
    };
    if (usedInterpreter === true) {
      upsertStr(FR_KEYS.family, family);
      upsertStr(FR_KEYS.given, given);
      upsertStr(FR_KEYS.org, org);
      upsertStr(FR_KEYS.phone, phone);
      upsertStr(FR_KEYS.mobile, mobile);
      upsertStr(FR_KEYS.email, email);
      upsertStr(FR_KEYS.language, language);
      upsertStr(FR_KEYS.signatureDate, signatureDate);
    }
    const errors = await runUpserts(upserts);
    if (errors.length > 0) {
      setSaveErrors(errors);
      return;
    }
    navigate(`/cases/${id}/wizard/preparer`);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Interpreter</h1>
        <p className="text-sm text-slate-500 mt-1">
          USCIS asks whether someone interpreted the form for you. Most
          applicants who read English well skip this section.
        </p>
      </header>

      <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <legend className="font-medium text-slate-900 px-1">
          Did you use an interpreter to fill out this form?
        </legend>
        <YesNo value={usedInterpreter} onChange={setUsedInterpreter} />
      </fieldset>

      {usedInterpreter === true && (
        <fieldset className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <legend className="font-medium text-slate-900 px-1">Interpreter details</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Family (last) name"
              required
              error={frFieldErrors?.[FR_KEYS.family]?.message}
            >
              <TextInput value={family} onChange={(e) => setFamily(e.target.value)} />
            </Field>
            <Field
              label="Given (first) name"
              required
              error={frFieldErrors?.[FR_KEYS.given]?.message}
            >
              <TextInput value={given} onChange={(e) => setGiven(e.target.value)} />
            </Field>
            <Field
              label="Business or organisation"
              hint="Leave blank if independent."
              className="sm:col-span-2"
              error={frFieldErrors?.[FR_KEYS.org]?.message}
            >
              <TextInput value={org} onChange={(e) => setOrg(e.target.value)} />
            </Field>
            <Field
              label="Daytime telephone"
              required
              error={frFieldErrors?.[FR_KEYS.phone]?.message}
            >
              <TextInput
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Field>
            <Field
              label="Mobile telephone"
              error={frFieldErrors?.[FR_KEYS.mobile]?.message}
            >
              <TextInput
                type="tel"
                value={mobile}
                onChange={(e) => setMobile(e.target.value)}
              />
            </Field>
            <Field
              label="Email address"
              className="sm:col-span-2"
              error={frFieldErrors?.[FR_KEYS.email]?.message}
            >
              <TextInput
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field
              label="Language interpreted"
              required
              hint="The language the interpreter used to communicate with you."
              error={frFieldErrors?.[FR_KEYS.language]?.message}
            >
              <TextInput
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                placeholder="Spanish"
              />
            </Field>
            <Field
              label="Date of interpreter's signature"
              required
              error={frFieldErrors?.[FR_KEYS.signatureDate]?.message}
            >
              <TextInput
                type="date"
                value={signatureDate}
                onChange={(e) => setSignatureDate(e.target.value)}
              />
            </Field>
          </div>
        </fieldset>
      )}

      {validationErr && (
        <p className="text-sm text-red-600">{validationErr}</p>
      )}
      {frFormError && (
        <p className="text-sm text-red-600">Could not save: {frFormError}</p>
      )}
      {frFieldErrors && (
        <p className="text-xs text-red-600">
          Some fields above need attention before continuing.
        </p>
      )}

      <div className="flex items-center justify-between pt-4 border-t border-slate-200">
        <Link
          to={`/cases/${id}/wizard/fee-reduction`}
          className="text-sm text-slate-600 hover:text-slate-900"
        >
          ← Fee reduction
        </Link>
        <button
          type="submit"
          disabled={fr.upsert.isPending}
          className="rounded-xl bg-slate-900 text-white px-5 py-2.5 text-sm font-medium hover:bg-slate-800 disabled:bg-slate-300"
        >
          {fr.upsert.isPending ? "Saving…" : "Save & continue"}
        </button>
      </div>
    </form>
  );
}
